import type { CallDiagnosticSink } from './direct-call-diagnostics';
import { HumanCallTransport } from './direct-call-connect';
import { HumanConversationHttp } from './direct-call-http';
import type { HumanCallRequest } from './direct-call-http';
import type { HttpClient } from '@microsoft/signalr';
import { HumanCallNotifications } from './direct-call-notifications';
import { SharedHumanCallNotifications } from './direct-call-shared-notifications';
import { ConversationSpeechSource } from './direct-call-speech-source';
import { DirectCallStreamingSpeechAdapter } from './direct-call-stream';
import type { DirectCallPcmSink } from './direct-call-stream';
import { createHumanRinger } from './direct-call-ringing';
import type { HumanRinger } from './direct-call-ringing';
import { FrameRequestFeed, HumanCallHttp } from './direct-call-frames';
import { HumanRtcHttp, RtcSignalFeed } from './direct-call-rtc';

/** Platform supplies raw HTTP and HUMAN token acquisition, never an Agent provider. */
export function createHumanCallTransport(options: {
  bridgeApi: string;
  appId: string;
  humanToken: (signal: AbortSignal) => Promise<string>;
  request: HumanCallRequest;
  hubHttpClient?: HttpClient;
  webSocket?: typeof WebSocket;
}): HumanCallTransport {
  const api = new HumanConversationHttp(
    options.bridgeApi,
    options.humanToken,
    options.request
  );
  return new HumanCallTransport(
    options.appId,
    api,
    () =>
      new HumanCallNotifications(
        options.bridgeApi,
        options.humanToken,
        api,
        options.hubHttpClient,
        options.webSocket
      )
  );
}

export function createHumanCallClient(options: {
  bridgeApi: string;
  appId: string;
  humanToken: (signal: AbortSignal) => Promise<string>;
  request: HumanCallRequest;
  hubHttpClient?: HttpClient;
  webSocket?: typeof WebSocket;
  sink: DirectCallPcmSink;
  decodeBase64: (value: string) => Uint8Array;
}): {
  transport: HumanCallTransport;
  speech: DirectCallStreamingSpeechAdapter;
  listSeats: HumanConversationHttp['listSeats'];
  /** US 36579: call-invite routes and the incomingCall hub, on the human token. */
  ringing: HumanRinger;
  diagnostics: CallDiagnosticSink;
  /** WI 35510: upload a pending attachment, then pass its id to session.send. */
  attachments: {
    upload: HumanConversationHttp['uploadAttachment'];
    download: HumanConversationHttp['downloadAttachment'];
  };
  /** US 37331: a seat's ask to see what the human is showing, and the answer. */
  frames: { on: FrameRequestFeed['on']; post: HumanCallHttp['postFrame'] };
  /** US 37334: peer-to-peer WebRTC signaling between the humans in a call (feeds RtcMesh). */
  rtc: {
    on: RtcSignalFeed['on'];
    config: HumanRtcHttp['getRtcConfig'];
    signal: HumanRtcHttp['postSignal'];
    reportIce: HumanRtcHttp['reportIceOutcome'];
  };
} {
  const api = new HumanRtcHttp(
    options.bridgeApi,
    options.humanToken,
    options.request
  );
  const frameFeed = new FrameRequestFeed();
  const rtcFeed = new RtcSignalFeed();
  // WI 33903 AC3: one hub for every open conversation (the visible call and
  // the background thread feeds); a lease never closes another's channel.
  const shared = new SharedHumanCallNotifications(
    () =>
      new HumanCallNotifications(
        options.bridgeApi,
        options.humanToken,
        api,
        options.hubHttpClient,
        options.webSocket
      ),
    (hub) => {
      frameFeed.attach(hub);
      rtcFeed.attach(hub);
    }
  );
  const transport = new HumanCallTransport(options.appId, api, () =>
    shared.lease()
  );
  const source = new ConversationSpeechSource(
    api,
    {
      get bindingId() {
        return shared.current?.bindingId ?? null;
      },
      waitForBinding: (signal) => {
        const active = shared.current;
        if (!active)
          return Promise.reject(new Error('Call connection unavailable'));
        return active.waitForBinding(signal);
      },
      subscribeSpeech: (receive) => {
        const active = shared.current;
        if (!active) throw new Error('Call connection unavailable');
        return active.subscribeSpeech(receive);
      },
    },
    options.decodeBase64
  );
  return {
    transport,
    diagnostics: api.diagnosticSink(options.appId),
    listSeats: (signal) => api.listSeats(signal),
    ringing: createHumanRinger(options, api),
    attachments: {
      upload: (id, upload, signal) => api.uploadAttachment(id, upload, signal),
      download: (id, attachmentId, signal) =>
        api.downloadAttachment(id, attachmentId, signal),
    },
    frames: {
      on: (handler) => frameFeed.on(handler),
      post: (id, requestId, frame, signal) =>
        api.postFrame(id, requestId, frame, signal),
    },
    rtc: {
      on: (handler) => rtcFeed.on(handler),
      config: (id, signal) => api.getRtcConfig(id, signal),
      signal: (id, to, type, payload, signal) =>
        api.postSignal(id, to, type, payload, signal),
      reportIce: (id, to, report, signal) =>
        api.reportIceOutcome(id, to, report, signal),
    },
    speech: new DirectCallStreamingSpeechAdapter(source, options.sink),
  };
}
